import { Icon } from "@blockdaemon-internal/design-system";
import { Trans } from "@lingui/react/macro";
import { Alert, Button, Flex, Stack } from "@mantine/core";
import * as Sentry from "@sentry/react";
import { OctagonAlert, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

interface ErrorAlertProps {
  title: string;
  children: ReactNode;
  error?: Error | { message: string };
}

export function ErrorAlert({ title, children, error }: ErrorAlertProps) {
  const hasLoggedError = useRef(false);

  // Automatically log error to Sentry when component mounts
  useEffect(() => {
    if (error && !hasLoggedError.current) {
      hasLoggedError.current = true;

      Sentry.withScope((scope) => {
        scope.setTag("errorSource", "ErrorAlert");
        scope.setTag("errorLocation", "user_facing_error");
        scope.setContext("errorAlert", {
          title,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent,
        });

        // Capture the error
        if (error instanceof Error) {
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(`ErrorAlert: ${error.message}`, "error");
        }
      });
    }
  }, [error, title]);

  const handleRefresh = async () => {
    window.location.reload();
  };

  return (
    <Alert
      icon={<Icon icon={OctagonAlert} size="1.25rem" />}
      variant="error"
      title={title}
      w="100%"
    >
      <Stack gap="md">
        {/* Render children */}
        {children}

        {/* Action buttons */}
        <Flex gap="0.5rem">
          <Button
            size="xs"
            variant="black-outline"
            onClick={handleRefresh}
            leftSection={<Icon icon={RefreshCw} />}
            styles={{
              root: {
                color: "white !important",
                ":hover": {
                  borderColor: "white !important",
                },
                ":active": {
                  transform: "none",
                },
              },
            }}
          >
            <Trans>Refresh</Trans>
          </Button>
        </Flex>
      </Stack>
    </Alert>
  );
}
